import React, { useLayoutEffect } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useLocation, useOutlet } from "react-router-dom";

const EASE = [0.22, 1, 0.36, 1];

function resetScrollPosition(scrollRoot) {
  window.scrollTo(0, 0);
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
  if (scrollRoot?.current) scrollRoot.current.scrollTop = 0;
}

export function AnimatedOutlet({ fillHeight = false, scrollRootRef } = {}) {
  const outlet = useOutlet();
  const location = useLocation();
  const prefersReducedMotion = useReducedMotion();
  const wrapClass = fillHeight
    ? "flex h-full min-h-0 w-full max-w-full flex-1 flex-col"
    : "min-h-0 w-full max-w-full";

  useLayoutEffect(() => {
    resetScrollPosition(scrollRootRef);
  }, [location.pathname, scrollRootRef]);

  if (prefersReducedMotion) {
    return <div className={wrapClass}>{outlet}</div>;
  }

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        className={wrapClass}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.32, ease: EASE }}
      >
        {outlet}
      </motion.div>
    </AnimatePresence>
  );
}

export function MotionSection({ as = "section", className, children, disableReveal = false, ...props }) {
  const Component = motion[as] || motion.section;
  const prefersReducedMotion = useReducedMotion();

  if (prefersReducedMotion || disableReveal) {
    const StaticComponent = as;
    return (
      <StaticComponent className={className} {...props}>
        {children}
      </StaticComponent>
    );
  }

  return (
    <Component
      className={className}
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.14, margin: "0px 0px -12% 0px" }}
      transition={{ duration: 0.72, ease: EASE }}
      {...props}
    >
      {children}
    </Component>
  );
}
